import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePurchaseOrder } from '../../hooks/usePurchaseOrders';
import { useContacts } from '../../hooks/useContacts';
import { useInventory } from '../../hooks/useInventory';
import { useAuth } from '../../context/AuthContext';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { purchaseOrdersService, PurchaseOrderFormData, PurchaseOrderItemFormData } from '../../services/purchaseOrdersService';
import { storageService } from '../../services/storageService';
import { formatCurrency } from '../../utils/currency';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import SearchableSelect from '../../components/ui/SearchableSelect';
import ItemSelectionModal, { WarningModal } from '../../components/ui/ItemSelectionModal';

const PurchaseOrderForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { order, loading: orderLoading } = usePurchaseOrder(id || null);
  const { contacts, loading: contactsLoading } = useContacts();
  const { items: inventoryItems, loading: inventoryLoading } = useInventory();
  const { businessId } = useAuth();

  const [poNumber, setPoNumber] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<Array<{ name: string; url: string; size: number }>>([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [items, setItems] = useState<PurchaseOrderItemFormData[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [warningModal, setWarningModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  // Filter contacts to only show vendors
  const vendors = contacts.filter(contact => contact.type === 'vendor');

  // Initialize form with existing order data or generate PO number
  useEffect(() => {
    if (order) {
      setPoNumber(order.po_number);
      setVendorId(order.vendor_id);
      setPurchaseDate(order.purchase_date);
      setDescription(order.description || '');
      setDiscountAmount(order.discount_amount);
      setAttachmentUrls(order.attachment_urls || []);

      // Populate items from existing order
      if (order.items && order.items.length > 0) {
        const orderItems: PurchaseOrderItemFormData[] = order.items.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount || 0,
          total_price: item.total_price,
        }));
        setItems(orderItems);
      }
    } else if (!id) {
      // Generate PO number for new orders
      setPoNumber(purchaseOrdersService.generatePurchaseOrderNumber());
    }
  }, [order, id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);

      // Validation: Check if vendor is selected
      if (!vendorId || vendorId.trim() === '') {
        setWarningModal({
          isOpen: true,
          title: 'Vendor Not Selected',
          message: 'Please select a vendor before creating the purchase order. A vendor is required to process the purchase and update their account balance.'
        });
        setLoading(false);
        return;
      }

      // Validation: Check if items are added
      if (items.length === 0) {
        setWarningModal({
          isOpen: true,
          title: 'No Items Added',
          message: 'Please add at least one item to the purchase order. Items are required to calculate inventory updates and costs.'
        });
        setLoading(false);
        return;
      }

      // Validate that all items have an item selected
      const hasEmptyItems = items.some(item => !item.item_id);
      if (hasEmptyItems) {
        setWarningModal({
          isOpen: true,
          title: 'Incomplete Items',
          message: 'Please select an inventory item for all rows. Each item must be properly selected from your inventory.'
        });
        setLoading(false);
        return;
      }

      const orderData: PurchaseOrderFormData = {
        po_number: poNumber,
        vendor_id: vendorId,
        purchase_date: purchaseDate,
        description,
        discount_amount: discountAmount,
        attachment_urls: attachmentUrls,
        items,
      };

      // Get business ID from auth context
      if (!businessId) {
        throw new Error('Business ID is required. Please ensure you are logged in with a business account.');
      }

      let result;
      if (id) {
        // Update existing purchase order
        result = await purchaseOrdersService.updatePurchaseOrder(id, orderData, businessId);
      } else {
        // Create new purchase order
        result = await purchaseOrdersService.createPurchaseOrder(orderData, businessId);
      }

      if (result.error) {
        setError(result.error.message);
      } else {
        // Redirect to the newly created purchase order details page
        if (result.order?.id) {
          navigate(`/purchase-orders/${result.order.id}`);
        } else {
          navigate('/purchase-orders');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save purchase order');
    }

    setLoading(false);
  };

  const addItem = () => {
    setEditingIndex(null);
    setIsModalOpen(true);
  };

  const handleItemSelect = (selectedItem: any) => {
    const selectedItemIds = items.map(item => item.item_id).filter(id => id !== '');

    if (selectedItemIds.includes(selectedItem.id)) {
      setError('This item is already added to the purchase order');
      return;
    }

    if (editingIndex !== null) {
      // Editing existing item
      updateItem(editingIndex, 'item_id', selectedItem.id);
    } else {
      // Adding new item
      setItems([
        ...items,
        {
          item_id: selectedItem.id,
          item_name: selectedItem.name,
          quantity: 1,
          unit_price: selectedItem.purchase_cost || selectedItem.average_cost,
          discount: 0,
          total_price: selectedItem.purchase_cost || selectedItem.average_cost
        }
      ]);
    }
  };

  const openChangeModal = (index: number) => {
    setEditingIndex(index);
    setIsModalOpen(true);
  };

  const removeItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const updateItem = (index: number, field: keyof PurchaseOrderItemFormData, value: any) => {
    const newItems = [...items];

    if (field === 'item_id') {
      // Find the selected item
      const selectedItem = inventoryItems.find(item => item.id === value);
      if (selectedItem) {
        // Check if item is already selected
        const isDuplicate = newItems.some((item, i) => i !== index && item.item_id === value);
        if (isDuplicate) {
          setError('This item is already added to the purchase order');
          return;
        }

        newItems[index] = {
          ...newItems[index],
          item_id: value,
          item_name: selectedItem.name,
          unit_price: selectedItem.purchase_cost || selectedItem.average_cost,
          total_price: (selectedItem.purchase_cost || selectedItem.average_cost) * newItems[index].quantity - newItems[index].discount
        };
      }
    } else if (field === 'quantity' || field === 'unit_price' || field === 'discount') {
      newItems[index] = { ...newItems[index], [field]: value };
      // Recalculate total price
      newItems[index].total_price = newItems[index].quantity * newItems[index].unit_price - newItems[index].discount;
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }

    setItems(newItems);
  };

  // Handle file uploads
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !businessId) return;

    setUploadingAttachments(true);
    const newAttachments: Array<{ name: string; url: string; size: number }> = [];
    const newUrls: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          setError(`File ${file.name} is too large. Maximum size is 10MB.`);
          continue;
        }

        // Upload file
        const result = await storageService.uploadPurchaseOrderAttachment(file, businessId, poNumber);

        if (result.error) {
          setError(`Failed to upload ${file.name}: ${result.error.message}`);
          continue;
        }

        if (result.url) {
          newAttachments.push({
            name: file.name,
            url: result.url,
            size: file.size
          });
          newUrls.push(result.url);
        }
      }

      // Update state
      setAttachments(prev => [...prev, ...newAttachments]);
      setAttachmentUrls(prev => [...prev, ...newUrls]);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload files');
    }

    setUploadingAttachments(false);
    // Reset file input
    event.target.value = '';
  };

  // Remove attachment
  const removeAttachment = async (index: number) => {
    const attachment = attachments[index];

    try {
      // Delete from storage
      const { error } = await storageService.deletePurchaseOrderAttachment(attachment.url);
      if (error) {
        console.error('Failed to delete attachment from storage:', error);
        // Continue with local removal even if storage deletion fails
      }

      // Remove from local state
      setAttachments(prev => prev.filter((_, i) => i !== index));
      setAttachmentUrls(prev => prev.filter((_, i) => i !== index));

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove attachment');
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const subtotal = items.reduce((sum, item) => sum + item.total_price, 0);
  const totalAmount = subtotal - discountAmount;

  if (orderLoading || contactsLoading || inventoryLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Purchase Orders', href: '/purchase-orders' },
    { label: id ? 'Edit Purchase Order' : 'Create Purchase Order', current: true }
  ];

  return (
    <div>
      {/* Breadcrumbs */}
      <Breadcrumb items={breadcrumbItems} />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {id ? 'Edit Purchase Order' : 'Create New Purchase Order'}
        </h1>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit} className="px-4 py-5 sm:p-6">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 mb-6">
            <Input
              label="Purchase Number"
              type="text"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              required
              disabled={!!id} // Only disabled when editing existing purchase
              placeholder={id ? '' : 'Enter purchase order number...'}
            />

            <Input
              label="Purchase Date"
              type="date"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              required
            />

            <SearchableSelect
              label="Vendor"
              value={vendorId}
              onChange={setVendorId}
              placeholder="Search and select a vendor..."
              required
              options={vendors.map(vendor => ({
                value: vendor.id,
                label: vendor.name,
                sublabel: vendor.contact_id ? `ID: ${vendor.contact_id}` : undefined
              }))}
            />
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center">
                <h2 className="text-lg font-medium text-gray-900">Items</h2>
                <span className="text-red-500 ml-1">*</span>
                <span className="text-sm text-gray-500 ml-2">(at least one item required)</span>
              </div>
              <button
                type="button"
                onClick={addItem}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Item
              </button>
            </div>

            <div className="border border-gray-200 rounded-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">
                      Item
                    </th>
                    <th className="px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                      Qty
                    </th>
                    <th className="px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                      Unit Price
                    </th>
                    <th className="px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                      Disc
                    </th>
                    <th className="px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                      Total
                    </th>
                    <th className="px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-2 py-6 text-center text-gray-500">
                        No items selected
                      </td>
                    </tr>
                  ) : (
                    items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-2 py-3 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {item.item_name || 'No item selected'}
                            </div>
                            {item.item_id && (
                              <div className="text-xs text-gray-500">
                                {(() => {
                                  const selectedItem = inventoryItems.find(inv => inv.id === item.item_id);
                                  return selectedItem?.sku ? `SKU: ${selectedItem.sku}` : '';
                                })()}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-1 py-3 whitespace-nowrap">
                          <input
                            type="number"
                            className="input-field w-20"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', Number(e.target.value))}
                            min="1"
                            required
                          />
                        </td>
                        <td className="px-1 py-3 whitespace-nowrap">
                          <input
                            type="number"
                            className="input-field w-24"
                            value={item.unit_price}
                            onChange={(e) => updateItem(index, 'unit_price', Number(e.target.value))}
                            min="0"
                            step="1000"
                            required
                          />
                        </td>
                        <td className="px-1 py-3 whitespace-nowrap">
                          <input
                            type="number"
                            className="input-field w-20 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                            value={item.discount}
                            onChange={(e) => updateItem(index, 'discount', Number(e.target.value))}
                            min="0"
                            step="1000"
                          />
                        </td>
                        <td className="px-1 py-3 whitespace-nowrap font-medium">
                          {formatCurrency(item.total_price)}
                        </td>
                        <td className="px-1 py-3 whitespace-nowrap">
                          <div className="flex items-center space-x-1">
                            <button
                              type="button"
                              onClick={() => openChangeModal(index)}
                              className="text-primary-600 hover:text-primary-900 p-1"
                              title="Change item"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="Remove item"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mb-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Purchase Description
                  </label>
                  <textarea
                    className="input-field"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter purchase order description..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attachments (Images/PDFs)
                  </label>
                  <div className="text-sm text-gray-500 mb-2">
                    Upload supporting documents, invoices, or images (max 10MB each)
                  </div>

                  {/* File Upload Input */}
                  <div className="mb-3">
                    <input
                      type="file"
                      accept="image/*,.pdf,.doc,.docx"
                      multiple
                      onChange={handleFileUpload}
                      className="input-field"
                      disabled={uploadingAttachments}
                    />
                    {uploadingAttachments && (
                      <div className="mt-2 text-sm text-blue-600">
                        Uploading files...
                      </div>
                    )}
                  </div>

                  {/* Uploaded Files List */}
                  {attachments.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-gray-700">Uploaded Files:</div>
                      {attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                          <div className="flex items-center space-x-2">
                            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <div>
                              <div className="text-sm font-medium text-gray-900 truncate max-w-48">
                                {attachment.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatFileSize(attachment.size)}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <a
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-900 p-1"
                              title="Download"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </a>
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="Remove"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Order Summary
                </label>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Subtotal</span>
                      <span className="text-base font-medium text-gray-900">
                        {formatCurrency(subtotal)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Discount</span>
                      <input
                        type="number"
                        className="input-field w-24 text-right"
                        value={discountAmount}
                        onChange={(e) => setDiscountAmount(Number(e.target.value))}
                        min="0"
                        step="1000"
                        placeholder="0"
                      />
                    </div>
                    <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                      <span className="text-sm font-medium text-gray-900">Total</span>
                      <span className="text-lg font-bold text-gray-900">
                        {formatCurrency(totalAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/purchase-orders')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Purchase Order'}
            </Button>
          </div>
        </form>
      </div>

      {/* Item Selection Modal */}
      <ItemSelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelect={handleItemSelect}
        items={inventoryItems}
        selectedItemIds={items.map(item => item.item_id).filter(id => id !== '')}
        title={editingIndex !== null ? 'Change Inventory Item' : 'Add Inventory Item'}
      />

      {/* Warning Modal */}
      <WarningModal
        isOpen={warningModal.isOpen}
        onClose={() => setWarningModal({ ...warningModal, isOpen: false })}
        title={warningModal.title}
        message={warningModal.message}
        confirmText="Got it"
      />
    </div>
  );
};

export default PurchaseOrderForm;